import React, {
	FC,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import DataGrid, {
	Column,
	DataGridHandle,
	RenderEditCellProps,
	RenderRowProps,
	Row,
} from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import { DeleteOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, message, Popconfirm, Select, Tooltip } from 'antd';
import { ImportField, ImportRow } from './ImportGridTypes';
import { isRowFullyEmpty, validateRows } from './validateRows';

// Row height tuned to fit ~3 lines of wrapped body text (line-height 22px) plus
// vertical padding, so multi-line cell content is visible without scrolling
// within the cell for typical inputs.
const GRID_ROW_HEIGHT = 72;

// Column headers hold a single line of text, so they get their own height rather than
// inheriting rowHeight — which is what react-data-grid does when `headerRowHeight` is
// omitted, handing a one-line header the full 72px of a three-line data row and
// costing a whole row of visible grid. Both the row hit-test and the row-insert
// separator derive their offsets from this, via the `--rdg-header-row-height` custom
// property rdg sets from it.
const GRID_HEADER_HEIGHT = 40;

// Data columns must carry an explicit pixel width. With no width, react-data-grid
// falls back to CSS `auto`, which makes every column flex to fill the grid. During
// a drag rdg pins only the dragged column to pixels and leaves the rest flexing, so
// the freed/consumed space is redistributed across the *other* auto columns — the
// ones to the LEFT of the handle included. That slides the dragged column's left
// edge while rdg recomputes `newWidth = pointerX - left` on every pointermove, so a
// shrinking `left` inflates the width, which steals more space, which shrinks `left`
// further: a feedback loop that reads as the column ballooning uncontrollably.
// Fixed widths remove the flex entirely (Excel/Google Sheets model): resizing one
// column shifts the rest and leaves trailing space / horizontal scroll instead of
// reflowing them, so the drag tracks the pointer 1:1.
const MIN_COLUMN_WIDTH = 120;

// Floor for a *manual* drag-resize. Deliberately far below MIN_COLUMN_WIDTH (which
// only governs the auto-computed starting width): once the user grabs a resize
// handle they're squeezing a column they don't care about to get more room for the
// ones they do, so let them collapse it almost flat. 44px still leaves space for a
// couple of characters plus the fill handle, and rdg clamps the pointer at this
// value rather than fighting it.
const MIN_RESIZE_COLUMN_WIDTH = 44;

// Content-aware base width: roomy enough for the header label, clamped so very
// long labels don't open absurdly wide. This is the *starting* width — when the
// columns don't fill the grid they're scaled up proportionally to absorb the slack
// (see the fill logic in the `columns` memo) and the result is assigned as a FIXED
// pixel width, preserving the autofit-then-fixed, drag-tracks-1:1 behaviour above.
// `hasRequiredMarker` reserves room for the trailing red asterisk so a required
// column's label doesn't start out ellipsised by its own marker.
const getColumnBaseWidth = (label: string, hasRequiredMarker: boolean): number =>
	Math.max(MIN_COLUMN_WIDTH, Math.min(label.length * 8 + 48 + (hasRequiredMarker ? 10 : 0), 400));

// Combined width of the non-data frame columns: frozen S.No (60) + delete (40).
// Subtracted from the grid's viewport width to find the space the data columns
// should share when filling.
const FRAME_COLUMNS_WIDTH = 100;

// One clipboard payload: a rectangular block of cell values.
// `values[rowOffset][colOffset]` lines up with `columnKeys[colOffset]`, so a paste only
// has to offset the block from the target cell. A single-cell copy is just a 1x1 block —
// there is no separate model for it.
interface CopiedRange {
	columnKeys: string[];
	values: any[][];
}

// Text form of a cell for the system clipboard. The list-shaped custom columns
// (checklist items, multiple choices, follow-up actions) have no useful string form, so
// they go out as JSON — readable enough for a human, and an intra-grid paste reads the
// in-memory block above rather than this text.
const cellValueToText = (value: any): string => {
	if (value === undefined || value === null) return '';
	if (typeof value !== 'object') return String(value);
	try {
		return JSON.stringify(value);
	} catch (stringifyErr) {
		return '';
	}
};

// Excel's TSV escaping: a field containing a tab, a newline or a quote is wrapped in
// double quotes with any inner quote doubled. Without it a multiline cell — the text
// columns are deliberately multiline — would break the row and column structure the
// moment it is pasted into a spreadsheet.
const toTsvField = (text: string): string =>
	/[\t\r\n"]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;

const buildTsv = (values: any[][]): string =>
	values
		.map((rowValues) => rowValues.map((value) => toTsvField(cellValueToText(value))).join('\t'))
		.join('\n');

// A filled cell must not share a reference with the cell it was filled from: the
// list-shaped custom columns (checklist items, multiple choices, follow-up
// actions) hold arrays of objects, so handing every filled row the same array
// would let a later edit to one row silently rewrite its neighbours. Primitives
// pass through untouched; anything structured is deep-copied.
const cloneFillValue = (value: any): any => {
	if (value === null || typeof value !== 'object') return value;
	try {
		return JSON.parse(JSON.stringify(value));
	} catch (cloneErr) {
		return value;
	}
};

// A fill in flight. Row indices are positions in the *displayed* row list (which the
// "show errors only" filter can narrow), so a drag always spans exactly what the user
// can see between the handle and the cursor. Column indices are positions in `fields`:
// a fill started from a multi-column selection writes every column in that selection.
interface FillDragState {
	firstColIdx: number;
	lastColIdx: number;
	sourceRowIdx: number;
	targetRowIdx: number;
	// Top row of the selection the fill started from. The handle only ever renders on that
	// block's bottom-right cell, so `sourceRowIdx` is its *last* row and says nothing
	// about how far up it reached — this is what lets the fill leave the whole block
	// selected afterwards instead of just the row it copied from.
	blockFirstRowIdx: number;
	// Dragging the handle *upwards* fills back over the rows above it. That is the
	// established behaviour for a single-cell selection, but on a multi-row selection it
	// would silently overwrite rows the user had only just selected, so it is offered
	// for the single-cell case alone.
	allowFillAbove: boolean;
}

// One fill to apply: which columns it spans, the row it copies from, the row it reaches,
// and the top of the block it started from. Passed as an object rather than four
// positional row/column numbers, which are trivially transposable at a call site.
interface FillRequest {
	firstColIdx: number;
	lastColIdx: number;
	sourceRowIdx: number;
	targetRowIdx: number;
	blockFirstRowIdx: number;
}

// The selected block of data cells, in the same frame of reference as FillDragState:
// rows are positions in the displayed row list, columns are positions in `fields`.
//
// The anchor and the focus are kept separate rather than pre-sorted. The anchor is the
// cell the gesture started from and stays put while a drag, a shift-click or a
// shift-arrow moves the focus, which is what lets a selection be extended upwards or
// leftwards without the block flipping about. Sorted bounds are derived on read.
interface CellRange {
	anchorRowIdx: number;
	anchorColIdx: number;
	focusRowIdx: number;
	focusColIdx: number;
}

// Sorted, in-bounds bounds of a rectangular block of data cells. Two things are shaped
// like this: the live selection and the outline round the last copied block.
interface CellBlockBounds {
	firstRowIdx: number;
	lastRowIdx: number;
	firstColIdx: number;
	lastColIdx: number;
}

// A CellRange resolved to sorted, in-bounds coordinates. Clamping lives here rather
// than at every write: rows come and go (add / insert / delete) while a selection is
// live, and a stale index has to degrade to the nearest real cell instead of reading
// past the end of the grid.
interface NormalisedCellRange extends CellBlockBounds {
	anchorRowIdx: number;
	anchorColIdx: number;
}

const normaliseCellRange = (
	range: CellRange | null,
	rowCount: number,
	columnCount: number
): NormalisedCellRange | null => {
	if (!range || rowCount <= 0 || columnCount <= 0) return null;
	const clampRowIdx = (rowIdx: number) => Math.max(0, Math.min(rowIdx, rowCount - 1));
	const clampColIdx = (colIdx: number) => Math.max(0, Math.min(colIdx, columnCount - 1));
	const anchorRowIdx = clampRowIdx(range.anchorRowIdx);
	const anchorColIdx = clampColIdx(range.anchorColIdx);
	const focusRowIdx = clampRowIdx(range.focusRowIdx);
	const focusColIdx = clampColIdx(range.focusColIdx);
	return {
		firstRowIdx: Math.min(anchorRowIdx, focusRowIdx),
		lastRowIdx: Math.max(anchorRowIdx, focusRowIdx),
		firstColIdx: Math.min(anchorColIdx, focusColIdx),
		lastColIdx: Math.max(anchorColIdx, focusColIdx),
		anchorRowIdx,
		anchorColIdx,
	};
};

const isSingleCellRange = (range: NormalisedCellRange): boolean =>
	range.firstRowIdx === range.lastRowIdx && range.firstColIdx === range.lastColIdx;

// A range drag in flight. `promoted` flips the first time the pointer reaches a cell
// other than the one it went down on. Until then the press is still an ordinary click,
// which is what keeps click-to-select, click-again-to-edit and the enum dropdown
// behaving exactly as they did before drag select existed.
interface RangeDragState {
	anchorRowIdx: number;
	anchorColIdx: number;
	promoted: boolean;
}

// react-data-grid sets aria-colindex to `column.idx + 1` on both header and body cells.
// The frozen S.No column always holds column.idx 0, so `fields[i]` lands at
// aria-colindex `i + 2`. Used to translate a hit-tested cell back into a field index —
// rdg virtualises columns horizontally, so DOM order is not the column index.
const DATA_COLUMN_ARIA_OFFSET = 2;

// How close to the grid's top or bottom edge a drag has to get before the viewport
// starts scrolling itself, and how far it then travels per tick. A held drag needs the
// interval because mousemove stops firing once the pointer parks in the edge band.
const AUTO_SCROLL_EDGE_PX = 36;
const AUTO_SCROLL_STEP_PX = 24;
const AUTO_SCROLL_INTERVAL_MS = 32;

// Keyboard range extension: shift + arrow moves the selection's focus by one cell.
const SELECTION_ARROW_DELTAS: { [key: string]: { rowDelta: number; colDelta: number } } = {
	ArrowUp: { rowDelta: -1, colDelta: 0 },
	ArrowDown: { rowDelta: 1, colDelta: 0 },
	ArrowLeft: { rowDelta: 0, colDelta: -1 },
	ArrowRight: { rowDelta: 0, colDelta: 1 },
};

interface EditableImportGridProps {
	fields: ImportField[];
	rows: ImportRow[];
	onRowsChange: (rows: ImportRow[]) => void;
	recordHook?: (record: any) => any;
	// Whether the user can append/insert rows. Defaults to true; set false when the row set is
	// fixed (e.g. editing a value per existing record) so adding a blank row makes no sense.
	allowAddRows?: boolean;
	// Whether the user can delete rows. Defaults to true; set false for a fixed row set where
	// removing a record makes no sense in context.
	allowDeleteRows?: boolean;
}

// An enum-list cell holds an array of option values, but a CSV import lands a raw
// comma-separated string in it before any recordHook normalises it. Accept both so
// the cell renders and edits correctly at either stage.
const toEnumListValues = (raw: any): any[] => {
	if (Array.isArray(raw)) return raw;
	if (raw === undefined || raw === null || raw === '') return [];
	return String(raw)
		.split(',')
		.map((entry) => entry.trim())
		.filter(Boolean);
};

const MultiSelectEditor = ({ row, column, onRowChange, onClose, options }: any) => (
	<div className="import-grid-enum-editor">
		<Select
			autoFocus
			defaultOpen
			showSearch
			mode="multiple"
			bordered={false}
			value={toEnumListValues(row[column.key])}
			placeholder="Search..."
			filterOption={(input, option) =>
				String((option && option.children) || '')
					.toLowerCase()
					.includes(input.toLowerCase())
			}
			onChange={(values) => onRowChange({ ...row, [column.key]: values }, false)}
			onBlur={() => onClose(true)}
			dropdownStyle={{ zIndex: 2000 }}
		>
			{options.map((option) => (
				<Select.Option key={option.value} value={option.value}>
					{option.label}
				</Select.Option>
			))}
		</Select>
	</div>
);

const SelectEditor = ({ row, column, onRowChange, onClose, options }: any) => (
	<div className="import-grid-enum-editor">
		<Select
			autoFocus
			defaultOpen
			showSearch
			bordered={false}
			value={row[column.key] !== undefined && row[column.key] !== '' ? row[column.key] : undefined}
			placeholder="Search..."
			filterOption={(input, option) =>
				String((option && option.children) || '')
					.toLowerCase()
					.includes(input.toLowerCase())
			}
			onChange={(val) => onRowChange({ ...row, [column.key]: val }, true)}
			onBlur={() => onClose(true)}
			dropdownStyle={{ zIndex: 2000 }}
		>
			{options.map((option) => (
				<Select.Option key={option.value} value={option.value}>
					{option.label}
				</Select.Option>
			))}
		</Select>
	</div>
);

const NumberEditor = ({ row, column, onRowChange, onClose }: RenderEditCellProps<ImportRow>) => (
	<input
		autoFocus
		type="number"
		className="rdg-text-editor h-full w-full border-none outline-none"
		value={row[column.key] !== undefined ? row[column.key] : ''}
		onChange={(event) => {
			const val = event.target.value;
			onRowChange({ ...row, [column.key]: val === '' ? '' : Number(val) });
		}}
		onBlur={() => onClose(true)}
	/>
);

// Multiline-friendly text editor. Enter commits the edit (matches the other
// editors and the Tab/Enter commit flow rdg expects); Shift+Enter inserts a
// newline so users can author wrapped, multi-paragraph values. Mirrors the
// Sheets / Notion convention for textarea-style cells.
const MultilineTextEditor = ({
	row,
	column,
	onRowChange,
	onClose,
}: RenderEditCellProps<ImportRow>) => {
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	// Size the textarea to its content rather than the full cell height. That
	// keeps the editor only as tall as the text, so the cell's `align-content:
	// center` vertically centers it exactly like the read-only content span —
	// the text stays put on entering edit mode instead of jumping to the top.
	const autoSizeTextarea = (textarea: HTMLTextAreaElement) => {
		textarea.style.height = 'auto';
		textarea.style.height = `${textarea.scrollHeight}px`;
	};
	// Focus + caret-to-end synchronously after mount so the user can keep
	// typing onto the existing value rather than at position 0. useLayoutEffect
	// avoids the visible blink that a post-paint useEffect would cause.
	useLayoutEffect(() => {
		const textarea = textareaRef.current;
		if (!textarea) return;
		autoSizeTextarea(textarea);
		textarea.focus();
		const caretPosition = textarea.value.length;
		textarea.setSelectionRange(caretPosition, caretPosition);
	}, []);
	const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		autoSizeTextarea(event.target);
		onRowChange({ ...row, [column.key]: event.target.value });
	};
	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			onClose(true);
		}
	};
	const handleBlur = () => onClose(true);
	const initialValue =
		row[column.key] === undefined || row[column.key] === null ? '' : String(row[column.key]);
	return (
		<textarea
			ref={textareaRef}
			className="import-grid-multiline-editor"
			rows={1}
			defaultValue={initialValue}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			onBlur={handleBlur}
		/>
	);
};

const CheckboxFormatter = ({ row, column, onRowChange }: any) => (
	<input
		type="checkbox"
		checked={!!row[column.key]}
		onChange={(event) => onRowChange({ ...row, [column.key]: event.target.checked })}
		className="m-auto block"
	/>
);

// Renders the cell value with a native `title` so hovering a truncated cell
// reveals the full content. Skips the title attribute when the value is empty
// so the browser doesn't show an empty bubble.
const CellTextContent: FC<{ value: any }> = ({ value }) => {
	const display = value === undefined || value === null || value === '' ? '' : String(value);
	if (display === '') {
		return <span className="import-grid-cell-content" />;
	}
	return (
		<span className="import-grid-cell-content" title={display}>
			{display}
		</span>
	);
};

const isFieldRequired = (field: ImportField): boolean =>
	!!field.constraints && field.constraints.some((constraint) => constraint.type === 'required');

// Column header: the field label plus a red `*` when the field carries a `required`
// constraint, matching how Ant Design marks required form labels. The label truncates
// with an ellipsis (and a native tooltip) so a narrowly-resized column keeps the
// asterisk visible instead of clipping it off the right edge.
const ImportGridHeaderCell: FC<{ field: ImportField }> = ({ field }) => (
	<div className="import-grid-header-content">
		<span className="import-grid-header-label" title={field.label}>
			{field.label}
		</span>
		{isFieldRequired(field) ? (
			<span className="import-grid-header-required" aria-hidden="true">
				*
			</span>
		) : null}
	</div>
);

const CellWithErrorTooltip = ({
	field,
	row,
	children,
}: {
	field: ImportField;
	row: ImportRow;
	children: React.ReactNode;
}) => {
	// Controlled so a click inside the cell can dismiss the tooltip. Without
	// this, opening an overlay (e.g. a custom-cell modal) covers the cell
	// without ever firing `mouseleave` on the anchor, so the popup hangs around
	// on top of the modal until the cursor physically leaves the cell.
	const [tooltipVisible, setTooltipVisible] = useState(false);
	const errs = row._errors && row._errors[field.key];
	if (errs && errs.length > 0) {
		const handleMouseDown = () => setTooltipVisible(false);
		return (
			<Tooltip
				title={errs.join(', ')}
				color="#ff4d4f"
				visible={tooltipVisible}
				onVisibleChange={setTooltipVisible}
			>
				<div className="h-full w-full" onMouseDown={handleMouseDown}>
					{children}
				</div>
			</Tooltip>
		);
	}
	return <>{children}</>;
};

const rowHasErrors = (row: ImportRow): boolean => {
	if (!row._errors) return false;
	return Object.values(row._errors).some((errors) => errors && errors.length > 0);
};

interface RowInsertSeparatorProps {
	rowIdx: number;
	rowId: number;
	onInsertRowAfter: (rowId: number) => void;
}

// Full-width hover zone straddling a row's bottom border. It's injected as an
// absolutely-positioned sibling of the row (via the grid's renderRow renderer)
// so it scrolls with the rows. Hovering anywhere along it highlights the
// separator line and reveals the insert-row button; clicking the button inserts
// a new row directly below this one. Vertical position is derived from the
// header height (exposed by react-data-grid as --rdg-header-row-height) plus the
// fixed row height, so it lands exactly on the boundary below row `rowIdx`.
const RowInsertSeparator: FC<RowInsertSeparatorProps> = ({ rowIdx, rowId, onInsertRowAfter }) => {
	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			onInsertRowAfter(rowId);
		},
		[rowId, onInsertRowAfter]
	);
	return (
		<div
			className="import-grid-row-separator"
			style={{
				top: `calc(var(--rdg-header-row-height, ${GRID_HEADER_HEIGHT}px) + ${
					(rowIdx + 1) * GRID_ROW_HEIGHT
				}px)`,
			}}
		>
			<button
				type="button"
				className="import-grid-insert-row-btn"
				onClick={handleClick}
				aria-label="Insert row below"
				title="Insert row below"
			>
				<PlusOutlined translate="" />
			</button>
		</div>
	);
};

// Which edges of the selected block a given cell sits on, so the range can be outlined
// as one rectangle instead of a grid of individually boxed cells.
// Which edges of a block a given cell sits on, so the block can be outlined as one
// rectangle instead of a grid of individually boxed cells.
interface CellBlockEdges {
	isTop: boolean;
	isRight: boolean;
	isBottom: boolean;
	isLeft: boolean;
}

interface CellSelectionEdges extends CellBlockEdges {
	// The cell the selection is anchored on — the "active" cell. It keeps a clear
	// background so the tint reads as what the selection *added* to it, matching Excel.
	isAnchor: boolean;
}

// The four edge classes are shared by both block overlays: they set only a border
// *width*, so whether the line is solid or dashed comes from the base class they sit
// alongside (.import-grid-range-highlight / .import-grid-copied-marquee).
const blockEdgeClasses = (edges: CellBlockEdges): string =>
	`${edges.isTop ? ' import-grid-block-edge-top' : ''}${
		edges.isRight ? ' import-grid-block-edge-right' : ''
	}${edges.isBottom ? ' import-grid-block-edge-bottom' : ''}${
		edges.isLeft ? ' import-grid-block-edge-left' : ''
	}`;

interface CellDecorationsProps {
	// Null when the cell is outside the selection, and also when the selection is a
	// single cell: react-data-grid already outlines its own focused cell, so drawing
	// ours on top of it would just double the border.
	selectionEdges: CellSelectionEdges | null;
	// Null when the cell is outside the block the last copy came from. Unlike the
	// selection above there is no single-cell exemption: nothing else on screen says
	// "this is what is on the clipboard", so even a 1x1 copy gets its marquee.
	copiedEdges: CellBlockEdges | null;
	isFillPreviewCell: boolean;
	showFillHandle: boolean;
	// The handle's overhang has to be given up at the grid's outer edges: an absolutely
	// positioned element still contributes to its scroll container's overflow, so a
	// handle hanging a few px past the last row (or the last column) would hand the grid
	// a scrollbar it does not otherwise need. rdg suppresses the same margins itself.
	isLastRow: boolean;
	isLastColumn: boolean;
	onFillStart: (event: React.MouseEvent) => void;
	onFillToLastRow: () => void;
}

// Everything painted *over* a data cell: the range-selection tint and outline, the
// dashed marquee round the last copied block, the fill drag preview, and the fill
// handle. All of them are absolutely positioned overlays, so none disturbs the cell's
// own content or layout.
//
// The fill handle is ours rather than react-data-grid's for every column. rdg only
// renders one where `column.renderEditCell != null`, which rules out the
// custom-rendered cells (checklist items, multiple choices, follow-up actions) and the
// checkbox cells — precisely the values that are most tedious to re-enter down a long
// grid. Owning it for all of them gives the grid one affordance instead of two, and it
// is the only way to know the full filled range afterwards so the fill can leave that
// range selected. Styled to match rdg's native handle exactly: it appears on the
// selection's bottom-right cell only (never on hover) and straddles that cell's corner
// rather than tucking inside it.
const CellDecorations: FC<CellDecorationsProps> = ({
	selectionEdges,
	copiedEdges,
	isFillPreviewCell,
	showFillHandle,
	isLastRow,
	isLastColumn,
	onFillStart,
	onFillToLastRow,
}) => {
	const handleDoubleClick = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			onFillToLastRow();
		},
		[onFillToLastRow]
	);
	// preventDefault on mousedown does not cancel the click that follows, so the press
	// would still bubble to the cell and select it. Swallow it here.
	const handleClick = useCallback((event: React.MouseEvent) => event.stopPropagation(), []);
	return (
		<>
			{selectionEdges ? (
				<span
					className={`import-grid-range-highlight${blockEdgeClasses(selectionEdges)}${
						selectionEdges.isAnchor ? ' import-grid-range-anchor' : ''
					}`}
				/>
			) : null}
			{isFillPreviewCell ? <span className="import-grid-fill-preview" /> : null}
			{/* After the tints, so the dashes stay readable over both of them. */}
			{copiedEdges ? (
				<span className={`import-grid-copied-marquee${blockEdgeClasses(copiedEdges)}`} />
			) : null}
			{showFillHandle ? (
				<span
					role="presentation"
					className={`import-grid-fill-handle${
						isLastRow ? ' import-grid-fill-handle-flush-block' : ''
					}${isLastColumn ? ' import-grid-fill-handle-flush-inline' : ''}`}
					onMouseDown={onFillStart}
					onClick={handleClick}
					onDoubleClick={handleDoubleClick}
					title="Drag to fill down; double-click to fill to the last row"
				/>
			) : null}
		</>
	);
};

const EditableImportGrid: FC<EditableImportGridProps> = ({
	fields,
	rows,
	onRowsChange,
	recordHook,
	allowAddRows = true,
	allowDeleteRows = true,
}) => {
	const [showErrorsOnly, setShowErrorsOnly] = useState(false);

	// The selected block of data cells — the grid's single selection model. A plain click
	// leaves a 1x1 block (which is also what offers the fill handle); a drag, a
	// shift-click or a shift-arrow grows it.
	const [selection, setSelection] = useState<CellRange | null>(null);
	// Mirrors `selection` synchronously so the window-level drag handlers read the live
	// block rather than whatever was captured when they were attached.
	const selectionRef = useRef<CellRange | null>(null);
	const writeSelection = useCallback((next: CellRange | null) => {
		selectionRef.current = next;
		setSelection(next);
	}, []);

	const selectSingleCell = useCallback(
		(rowIdx: number, columnIdx: number) => {
			const current = selectionRef.current;
			if (
				current &&
				current.anchorRowIdx === rowIdx &&
				current.focusRowIdx === rowIdx &&
				current.anchorColIdx === columnIdx &&
				current.focusColIdx === columnIdx
			) {
				return;
			}
			writeSelection({
				anchorRowIdx: rowIdx,
				anchorColIdx: columnIdx,
				focusRowIdx: rowIdx,
				focusColIdx: columnIdx,
			});
		},
		[writeSelection]
	);

	const [fillDrag, setFillDrag] = useState<FillDragState | null>(null);
	// Same reasoning as selectionRef: the drag handlers live on the window and must see
	// the current fill, not the one in scope when they were registered.
	const fillDragRef = useRef<FillDragState | null>(null);
	const writeFillDrag = useCallback((next: FillDragState | null) => {
		fillDragRef.current = next;
		setFillDrag(next);
	}, []);

	// A range drag lives entirely in a ref until it promotes — an un-promoted press is
	// indistinguishable from a click and must not re-render anything. `isRangeDragging`
	// exists only for the grid-level class that suppresses text selection.
	const rangeDragRef = useRef<RangeDragState | null>(null);
	const [isRangeDragging, setIsRangeDragging] = useState(false);
	// Last known pointer position, so the auto-scroll interval below can re-derive the
	// drag target while the pointer itself sits still.
	const dragPointerRef = useRef<{ clientX: number; clientY: number } | null>(null);
	const suppressNextCellClickRef = useRef(false);

	// The block the last copy came from, outlined with Excel's dashed marquee so it stays
	// obvious what is on the clipboard after the selection has moved on. Deliberately
	// separate from copiedRangeRef below: that holds the *values* and stays valid for
	// pasting no matter what happens to the rows, whereas this is purely positional and
	// goes stale as soon as the displayed rows change.
	const [copiedBlock, setCopiedBlock] = useState<CellBlockBounds | null>(null);

	// Both the selection and the copy marquee address cells by position in the displayed
	// row list, so anything that changes which rows are displayed — or in what order —
	// invalidates the two together. Kept as one helper so a future row operation cannot
	// clear one and strand the other.
	const clearCellBlocks = useCallback(() => {
		writeSelection(null);
		setCopiedBlock(null);
	}, [writeSelection]);

	// react-data-grid (7.0.0-beta.47) exposes no resize start/end event or
	// "resizing" class — only a continuous onColumnResize(idx, width) during the
	// drag. Track the column being resized so we can paint a full-height black
	// separator across that whole column (header + every row) instead of just the
	// header handle.
	const [resizingColIdx, setResizingColIdx] = useState<number | null>(null);

	// Whether the grid's columns overflow its viewport. Drives the frozen S.No
	// column's drop shadow: hidden when everything fits (the shadow against a
	// flush edge just looks like grime), shown only once content can scroll
	// underneath it — the standard "sticky column casts a shadow over scrolled
	// content" affordance.
	const [isHorizontallyScrollable, setIsHorizontallyScrollable] = useState(false);

	// The grid viewport's current pixel width, used to distribute available space
	// across the data columns so they fill it instead of leaving trailing
	// whitespace. Seeded and kept current by the layout effect below (mount +
	// ResizeObserver). `null` until first measured — columns fall back to their
	// content-aware base widths for that initial frame.
	const [gridWidth, setGridWidth] = useState<number | null>(null);

	// Clear the highlight when the drag ends. `pointerup` alone is not enough:
	// rdg releases its pointer capture immediately after, and its
	// `lostpointercapture` handler runs one final onPointerMove → onColumnResize
	// when the end position differs from the last move (bundle.js:1411-1414). That
	// fires AFTER our pointerup and re-sets resizingColIdx, so the line lingered.
	// `lostpointercapture` bubbles, so handling it here runs in the bubble phase —
	// after rdg's target-phase re-set — and React batches both within that one
	// native event, letting the final null win. pointercancel covers aborted drags.
	useEffect(() => {
		const clearResizing = () => setResizingColIdx((prev) => (prev === null ? prev : null));
		window.addEventListener('pointerup', clearResizing);
		window.addEventListener('lostpointercapture', clearResizing);
		window.addEventListener('pointercancel', clearResizing);
		return () => {
			window.removeEventListener('pointerup', clearResizing);
			window.removeEventListener('lostpointercapture', clearResizing);
			window.removeEventListener('pointercancel', clearResizing);
		};
	}, []);

	const runValidation = useCallback(
		(updatedRows: ImportRow[]): ImportRow[] => validateRows(updatedRows, fields, recordHook),
		[fields, recordHook]
	);

	const handleRowsChange = useCallback(
		(newRows: ImportRow[]) => {
			if (!showErrorsOnly) {
				onRowsChange(runValidation(newRows));
				return;
			}
			// react-data-grid hands us back the FILTERED set with edits applied.
			// Merge those edits back into the full row list by `_rowId` so hidden
			// rows aren't dropped.
			const updatesById = new Map<number, ImportRow>();
			newRows.forEach((row) => updatesById.set(row._rowId, row));
			const merged = rows.map((row) => updatesById.get(row._rowId) || row);
			onRowsChange(runValidation(merged));
		},
		[onRowsChange, runValidation, rows, showErrorsOnly]
	);

	const handleToggleShowErrorsOnly = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setShowErrorsOnly(event.target.checked);
			// A selection addresses rows by position in the displayed list, and toggling the
			// filter re-shuffles which rows those positions point at. Drop it rather than
			// leave a block highlighting cells the user never picked.
			clearCellBlocks();
		},
		[clearCellBlocks]
	);

	const buildEmptyRow = useCallback((): ImportRow => {
		const newRow: ImportRow = {
			_rowId: Date.now() + Math.random(),
			_errors: {},
		};
		fields.forEach((field) => {
			newRow[field.key] = field.type === 'boolean' ? false : '';
		});
		return newRow;
	}, [fields]);

	// Imperative handle on the grid so we can scroll the freshly added row into
	// view. Set when a row is appended and consumed by the layout effect below,
	// which fires once the new row has actually rendered.
	const gridRef = useRef<DataGridHandle>(null);
	const scrollToBottomRef = useRef(false);

	// Compare the scroll container's content width against its visible width to
	// know whether the columns can scroll horizontally. Reads layout, so it runs
	// on mount, on container resize (ResizeObserver below), and after a column
	// drag-resize — never on every render.
	const measureHorizontalScroll = useCallback(() => {
		const element = gridRef.current && gridRef.current.element;
		if (!element) return;
		const scrollable = element.scrollWidth > element.clientWidth;
		setIsHorizontallyScrollable((prev) => (prev === scrollable ? prev : scrollable));
	}, []);

	// Read the grid viewport's visible width (clientWidth already excludes any
	// vertical scrollbar) so the column memo can stretch the data columns to fill
	// it. Equality-guarded so an unchanged width doesn't churn the columns memo.
	const measureGridWidth = useCallback(() => {
		const element = gridRef.current && gridRef.current.element;
		if (!element) return;
		const width = element.clientWidth;
		setGridWidth((prev) => (prev === width ? prev : width));
	}, []);

	const handleColumnResize = useCallback(
		(idx: number) => {
			setResizingColIdx((prev) => (prev === idx ? prev : idx));
			// Widening a column can push the total past the viewport; re-measure on
			// the next frame, after rdg has applied the new track width.
			requestAnimationFrame(measureHorizontalScroll);
		},
		[measureHorizontalScroll]
	);

	// Track horizontal scrollability for the frozen-column shadow. ResizeObserver
	// catches container width changes (window resize, panel collapse); the mount
	// pass seeds the initial state. Column drag-resizes are handled in
	// handleColumnResize since they change content width without resizing the box.
	// useLayoutEffect (not useEffect) so the first width measurement lands before
	// paint — the columns fill the grid on the initial frame instead of flashing
	// at their narrow base widths and then snapping wide.
	useLayoutEffect(() => {
		const element = gridRef.current && gridRef.current.element;
		const measure = () => {
			measureGridWidth();
			measureHorizontalScroll();
		};
		if (!element || typeof ResizeObserver === 'undefined') {
			measure();
			return undefined;
		}
		const observer = new ResizeObserver(measure);
		observer.observe(element);
		measure();
		return () => observer.disconnect();
	}, [measureGridWidth, measureHorizontalScroll, fields]);

	const handleAddRow = useCallback(() => {
		const updated = [...rows, buildEmptyRow()];
		// The new row carries no errors, so the "show errors only" filter would
		// hide it — reintroducing the very "did my click do anything?" confusion
		// we're fixing. Drop the filter so the appended row is always visible.
		setShowErrorsOnly(false);
		scrollToBottomRef.current = true;
		clearCellBlocks();
		onRowsChange(runValidation(updated));
		message.success('Row added');
	}, [buildEmptyRow, rows, onRowsChange, runValidation, clearCellBlocks]);

	const handleDeleteRow = useCallback(
		(rowId: number) => {
			const updated = rows.filter((row) => row._rowId !== rowId);
			clearCellBlocks();
			onRowsChange(runValidation(updated));
		},
		[rows, onRowsChange, runValidation, clearCellBlocks]
	);

	const handleInsertRowAfter = useCallback(
		(rowId: number) => {
			const insertAt = rows.findIndex((existingRow) => existingRow._rowId === rowId);
			const newRow = buildEmptyRow();
			const updated =
				insertAt === -1
					? [...rows, newRow]
					: [...rows.slice(0, insertAt + 1), newRow, ...rows.slice(insertAt + 1)];
			clearCellBlocks();
			onRowsChange(runValidation(updated));
		},
		[buildEmptyRow, rows, onRowsChange, runValidation, clearCellBlocks]
	);

	// Single shared slot for the most recently copied block. Stored in a ref so the
	// keydown handler can read fresh values without re-registering on every rows change,
	// and so copy/paste survives across react-data-grid's internal state transitions
	// (which it does not — it has no notion of our custom cells, so we own the clipboard
	// model entirely).
	const copiedRangeRef = useRef<CopiedRange | null>(null);

	const errorRowCount = useMemo(
		() => rows.reduce((count, row) => count + (rowHasErrors(row) ? 1 : 0), 0),
		[rows]
	);

	// Treat the "errors only" filter as inactive whenever there are no errors
	// to show, so the grid never goes silently empty after the user finishes
	// fixing them. Computed during render — not stored — so toggling back on
	// is instantaneous when new errors appear, and there is no frame where
	// `displayRows` is an empty filtered set waiting on an effect to reset.
	const effectiveShowErrorsOnly = showErrorsOnly && errorRowCount > 0;

	const displayRows = useMemo(
		() => (effectiveShowErrorsOnly ? rows.filter(rowHasErrors) : rows),
		[rows, effectiveShowErrorsOnly]
	);

	const columnIdxByKey = useMemo(() => {
		const map = new Map<string, number>();
		fields.forEach((field, index) => map.set(field.key, index));
		return map;
	}, [fields]);

	const normalisedSelection = useMemo(
		() => normaliseCellRange(selection, displayRows.length, fields.length),
		[selection, displayRows.length, fields.length]
	);

	// The cell that hosts the fill handle, keyed by _rowId because rdg's cellClass
	// callback only ever sees a row, never its index. That one cell is the only one to
	// opt out of rdg's overflow clipping, so the handle can straddle its corner.
	const fillHandleCell = useMemo(() => {
		if (!normalisedSelection) return null;
		const row = displayRows[normalisedSelection.lastRowIdx];
		if (!row) return null;
		return { rowId: row._rowId, columnIdx: normalisedSelection.lastColIdx };
	}, [displayRows, normalisedSelection]);

	// rdg's own selection drives the single-cell case, so clicking or arrow-keying onto a
	// cell selects it (and offers the fill handle) exactly as it did before.
	const handleSelectedCellChange = useCallback(
		(args) => {
			// rdg fires this with no `row` when the selection lands somewhere that is not a
			// data cell — the header row, or focus leaving the grid body (which is what an
			// antd dropdown rendered into a portal does on close). There is nothing to
			// select, so drop the signal rather than dereferencing undefined.
			if (!args || !args.row || !args.column) return;
			const columnIdx = columnIdxByKey.get(args.column.key);
			if (columnIdx === undefined) return;
			// While a press is being tracked, the gesture owns the block: our capture-phase
			// mousedown has already seeded it and rdg fires this immediately afterwards for
			// that same press, so letting it through would collapse a shift-extend (or the
			// block a drag is building) straight back to a single cell.
			if (rangeDragRef.current !== null) return;
			selectSingleCell(args.rowIdx, columnIdx);
		},
		[columnIdxByKey, selectSingleCell]
	);

	// Everything the overlay layer in one cell needs to know. Called once per rendered
	// cell, so it stays plain arithmetic against the selection and the fill in flight.
	const getCellDecorations = useCallback(
		(rowIdx: number, columnIdx: number) => {
			const range = normalisedSelection;
			// A single-cell selection is left to rdg, which already outlines its focused
			// cell — drawing ours over it would double the border.
			const isInBlock =
				range !== null &&
				!isSingleCellRange(range) &&
				rowIdx >= range.firstRowIdx &&
				rowIdx <= range.lastRowIdx &&
				columnIdx >= range.firstColIdx &&
				columnIdx <= range.lastColIdx;
			return {
				selectionEdges: isInBlock
					? {
							isTop: rowIdx === range.firstRowIdx,
							isRight: columnIdx === range.lastColIdx,
							isBottom: rowIdx === range.lastRowIdx,
							isLeft: columnIdx === range.firstColIdx,
							isAnchor: rowIdx === range.anchorRowIdx && columnIdx === range.anchorColIdx,
					  }
					: null,
				// No single-cell exemption here, unlike the selection above: the marquee is the
				// only thing that says what is on the clipboard.
				copiedEdges:
					copiedBlock !== null &&
					rowIdx >= copiedBlock.firstRowIdx &&
					rowIdx <= copiedBlock.lastRowIdx &&
					columnIdx >= copiedBlock.firstColIdx &&
					columnIdx <= copiedBlock.lastColIdx
						? {
								isTop: rowIdx === copiedBlock.firstRowIdx,
								isRight: columnIdx === copiedBlock.lastColIdx,
								isBottom: rowIdx === copiedBlock.lastRowIdx,
								isLeft: columnIdx === copiedBlock.firstColIdx,
						  }
						: null,
				showFillHandle:
					range !== null && rowIdx === range.lastRowIdx && columnIdx === range.lastColIdx,
				isFillPreviewCell:
					fillDrag !== null &&
					columnIdx >= fillDrag.firstColIdx &&
					columnIdx <= fillDrag.lastColIdx &&
					rowIdx !== fillDrag.sourceRowIdx &&
					rowIdx >= Math.min(fillDrag.sourceRowIdx, fillDrag.targetRowIdx) &&
					rowIdx <= Math.max(fillDrag.sourceRowIdx, fillDrag.targetRowIdx),
			};
		},
		[copiedBlock, fillDrag, normalisedSelection]
	);

	// Which row sits under the cursor. Every row is exactly GRID_ROW_HEIGHT tall, so the
	// index is plain arithmetic against the scroll container rather than DOM hit-testing —
	// which means it keeps working while the pointer is over the sticky header, a
	// row-insert separator, or anything else layered on top of the rows. Clamping to the
	// rendered rows is what makes a drag structurally incapable of running past the end of
	// the grid.
	const getRowIdxAtClientY = useCallback(
		(clientY: number): number | null => {
			const element = gridRef.current && gridRef.current.element;
			if (!element || displayRows.length === 0) return null;
			const headerHeight = parseFloat(
				window.getComputedStyle(element).getPropertyValue('--rdg-header-row-height')
			);
			const offsetIntoRows =
				clientY -
				element.getBoundingClientRect().top +
				element.scrollTop -
				(isNaN(headerHeight) ? 0 : headerHeight);
			const rowIdx = Math.floor(offsetIntoRows / GRID_ROW_HEIGHT);
			return Math.max(0, Math.min(rowIdx, displayRows.length - 1));
		},
		[displayRows.length]
	);

	// Which data column sits under the cursor. Column widths are not uniform (they are
	// content-derived, and the user can drag-resize them) and rdg virtualises columns
	// horizontally, so this measures the header cells that are actually mounted and reads
	// each one's aria-colindex rather than trusting DOM order or a computed width.
	const getColumnIdxAtClientX = useCallback(
		(clientX: number): number | null => {
			const element = gridRef.current && gridRef.current.element;
			if (!element || fields.length === 0) return null;
			let nearestColumnIdx: number | null = null;
			let nearestDistance = Infinity;
			element.querySelectorAll('[role="columnheader"]').forEach((headerCell) => {
				const columnIdx =
					Number(headerCell.getAttribute('aria-colindex')) - DATA_COLUMN_ARIA_OFFSET;
				if (isNaN(columnIdx) || columnIdx < 0 || columnIdx > fields.length - 1) return;
				const bounds = headerCell.getBoundingClientRect();
				// Anywhere outside the data columns — over the frozen S.No column, the delete
				// column, or the trailing slack — resolves to the nearest one, so a drag that
				// wanders sideways off the data area stops at the edge instead of going dead.
				let distance = 0;
				if (clientX < bounds.left) distance = bounds.left - clientX;
				else if (clientX > bounds.right) distance = clientX - bounds.right;
				if (distance < nearestDistance) {
					nearestDistance = distance;
					nearestColumnIdx = columnIdx;
				}
			});
			return nearestColumnIdx;
		},
		[fields.length]
	);

	// Copy the source row's values into every row between the handle and the drop target,
	// across every column the fill spans. `displayRows` is the whole universe here, so a
	// fill only ever rewrites rows that already exist — it can never append one.
	const applyFill = useCallback(
		({ firstColIdx, lastColIdx, sourceRowIdx, targetRowIdx, blockFirstRowIdx }: FillRequest) => {
			const sourceRow = displayRows[sourceRowIdx];
			if (!sourceRow) return;
			const firstRowIdx = Math.min(sourceRowIdx, targetRowIdx);
			const lastRowIdx = Math.max(sourceRowIdx, targetRowIdx);
			if (firstRowIdx === lastRowIdx) return;
			// A readonly column inside the span is skipped rather than aborting the whole
			// fill — a multi-column selection can easily straddle one.
			const fillableFields = fields
				.slice(firstColIdx, lastColIdx + 1)
				.filter((field) => !field.readonly);
			if (fillableFields.length === 0) return;
			const filledRows = displayRows.map((row, rowIdx) => {
				if (rowIdx === sourceRowIdx || rowIdx < firstRowIdx || rowIdx > lastRowIdx) return row;
				const filledRow: ImportRow = { ...row };
				fillableFields.forEach((field) => {
					filledRow[field.key] = cloneFillValue(sourceRow[field.key]);
				});
				return filledRow;
			});
			// handleRowsChange re-validates and, when the errors-only filter is on, merges
			// these visible rows back into the full list by _rowId.
			handleRowsChange(filledRows);
			// Leave the source block AND everything filled selected, the way a spreadsheet
			// does: the user can see exactly which cells are involved and copy them straight
			// away. Anchoring on `sourceRowIdx` instead would silently drop the rows above
			// the handle — a fill dragged down from a 3-row selection came back with only its
			// bottom row still selected.
			writeSelection({
				anchorRowIdx: Math.min(blockFirstRowIdx, targetRowIdx),
				anchorColIdx: firstColIdx,
				focusRowIdx: Math.max(sourceRowIdx, targetRowIdx),
				focusColIdx: lastColIdx,
			});
		},
		[displayRows, fields, handleRowsChange, writeSelection]
	);

	const handleFillStart = useCallback(
		(event: React.MouseEvent) => {
			if (event.button !== 0) return;
			const range = normalisedSelection;
			if (!range) return;
			// Keep the press off the cell underneath — it would select the cell or open an
			// inline dropdown — and out of the browser's text-selection machinery.
			event.preventDefault();
			event.stopPropagation();
			dragPointerRef.current = { clientX: event.clientX, clientY: event.clientY };
			// The handle only ever renders on the selection's bottom-right cell, so a fill
			// sources from the block's last row and spans the block's columns.
			writeFillDrag({
				firstColIdx: range.firstColIdx,
				lastColIdx: range.lastColIdx,
				sourceRowIdx: range.lastRowIdx,
				targetRowIdx: range.lastRowIdx,
				blockFirstRowIdx: range.firstRowIdx,
				allowFillAbove: range.firstRowIdx === range.lastRowIdx,
			});
		},
		[normalisedSelection, writeFillDrag]
	);

	// Double-clicking the handle fills from here to the last row — the shortcut rdg gave
	// its own handle, and the one Excel gives the fill handle.
	const handleFillToLastRow = useCallback(() => {
		const range = normalisedSelection;
		if (!range) return;
		applyFill({
			firstColIdx: range.firstColIdx,
			lastColIdx: range.lastColIdx,
			sourceRowIdx: range.lastRowIdx,
			targetRowIdx: displayRows.length - 1,
			blockFirstRowIdx: range.firstRowIdx,
		});
	}, [applyFill, displayRows.length, normalisedSelection]);

	// The one place either drag decides where it now reaches. Shared by the window
	// mousemove handler and the auto-scroll interval, so a drag extended by scrolling
	// behaves identically to one extended by moving the mouse.
	const extendDragToPointer = useCallback(
		(clientX: number, clientY: number) => {
			const rowIdx = getRowIdxAtClientY(clientY);
			if (rowIdx === null) return;
			const fill = fillDragRef.current;
			if (fill) {
				const targetRowIdx = fill.allowFillAbove ? rowIdx : Math.max(rowIdx, fill.sourceRowIdx);
				if (fill.targetRowIdx === targetRowIdx) return;
				writeFillDrag({ ...fill, targetRowIdx });
				return;
			}
			const drag = rangeDragRef.current;
			if (!drag) return;
			const columnIdx = getColumnIdxAtClientX(clientX);
			if (columnIdx === null) return;
			if (!drag.promoted) {
				// Still inside the cell the press landed in, so this is still a click.
				if (rowIdx === drag.anchorRowIdx && columnIdx === drag.anchorColIdx) return;
				rangeDragRef.current = { ...drag, promoted: true };
				setIsRangeDragging(true);
				// The browser may already have started a text selection across the cells the
				// pointer swept on its way out of the anchor cell. Drop it, so the grid's own
				// highlight is the only selection on screen — and so the Cmd+C guard below,
				// which defers to a live text selection, does not mistake it for one.
				const textSelection = window.getSelection();
				if (textSelection) textSelection.removeAllRanges();
			}
			const current = selectionRef.current;
			if (current && current.focusRowIdx === rowIdx && current.focusColIdx === columnIdx) return;
			writeSelection({
				anchorRowIdx: drag.anchorRowIdx,
				anchorColIdx: drag.anchorColIdx,
				focusRowIdx: rowIdx,
				focusColIdx: columnIdx,
			});
		},
		[getColumnIdxAtClientX, getRowIdxAtClientY, writeFillDrag, writeSelection]
	);

	const handleGridMouseDown = useCallback(
		(event: MouseEvent) => {
			// Either consumed or abandoned by the previous gesture — a new press starts clean.
			suppressNextCellClickRef.current = false;
			if (event.button !== 0) return;
			const target = event.target as HTMLElement;
			if (!target || typeof target.closest !== 'function') return;
			// Presses that belong to another gesture entirely: the fill handle runs its own
			// drag, the header owns resize, the row-insert separator owns its button, and an
			// open editor owns its own text.
			if (
				target.closest(
					'.import-grid-fill-handle, [role="columnheader"], .import-grid-row-separator, .rdg-editor-container'
				)
			) {
				return;
			}
			const cell = target.closest('[role="gridcell"]');
			if (!cell) return;
			const columnIdx = Number(cell.getAttribute('aria-colindex')) - DATA_COLUMN_ARIA_OFFSET;
			// The frozen S.No column and the delete column are frame, not data.
			if (isNaN(columnIdx) || columnIdx < 0 || columnIdx > fields.length - 1) return;
			const rowIdx = getRowIdxAtClientY(event.clientY);
			if (rowIdx === null) return;

			// A press on an inline control a cell renders — the Select inside a custom cell,
			// a checkbox — still *selects* that cell. That is the only way those columns get
			// a fill handle at all: rdg never selects a cell whose child swallows mousedown,
			// which FollowUpActionsField does deliberately so its Select can keep focus. It
			// must not arm a drag though, or operating the widget would sweep a selection
			// across the grid behind it.
			const isInlineControlPress =
				target.closest('input, textarea, select, button, .ant-select') !== null;

			// Shift-click extends the existing block from its anchor. Swallowing the event
			// keeps rdg from moving its own focused cell off that anchor, so the keyboard
			// shortcuts keep operating on the block the user is building.
			const current = selectionRef.current;
			if (event.shiftKey && current && !isInlineControlPress) {
				event.preventDefault();
				event.stopPropagation();
				writeSelection({
					anchorRowIdx: current.anchorRowIdx,
					anchorColIdx: current.anchorColIdx,
					focusRowIdx: rowIdx,
					focusColIdx: columnIdx,
				});
				return;
			}

			selectSingleCell(rowIdx, columnIdx);
			if (isInlineControlPress) return;
			// Deliberately NOT prevented: rdg still needs this press to select the cell and
			// take focus, which is what puts Cmd+C / Cmd+V in reach of onCellKeyDown at all.
			// The drag only takes over once the pointer reaches a different cell.
			rangeDragRef.current = { anchorRowIdx: rowIdx, anchorColIdx: columnIdx, promoted: false };
			dragPointerRef.current = { clientX: event.clientX, clientY: event.clientY };
		},
		[fields.length, getRowIdxAtClientY, selectSingleCell, writeSelection]
	);

	// Capture phase, on the grid element: FollowUpActionsField (and the other custom
	// cells) deliberately swallow mousedown so their inline Select can keep focus, so a
	// bubble-phase listener would never see a press on those columns at all.
	useEffect(() => {
		const element = gridRef.current && gridRef.current.element;
		if (!element) return undefined;
		element.addEventListener('mousedown', handleGridMouseDown, true);
		return () => element.removeEventListener('mousedown', handleGridMouseDown, true);
	}, [handleGridMouseDown]);

	// One drag pipeline for both gestures. Tracking on the window rather than the grid
	// matters because the pointer routinely leaves the grid mid-drag and a mouseup out
	// there still has to commit. Both handlers read the live drag out of refs and bail
	// straight away when nothing is in flight, so they are cheap to leave attached.
	useEffect(() => {
		const finishDrag = () => {
			const fill = fillDragRef.current;
			if (fill) {
				writeFillDrag(null);
				applyFill({
					firstColIdx: fill.firstColIdx,
					lastColIdx: fill.lastColIdx,
					sourceRowIdx: fill.sourceRowIdx,
					targetRowIdx: fill.targetRowIdx,
					blockFirstRowIdx: fill.blockFirstRowIdx,
				});
			}
			const drag = rangeDragRef.current;
			if (drag) {
				rangeDragRef.current = null;
				if (drag.promoted) {
					// A promoted drag still ends in a click on whichever cell the pointer was
					// released over. Swallow that one click, so releasing back inside the anchor
					// cell does not also open an editor on top of the block just selected.
					suppressNextCellClickRef.current = true;
					setIsRangeDragging(false);
				}
			}
			dragPointerRef.current = null;
		};
		const handleMouseMove = (event: MouseEvent) => {
			if (!fillDragRef.current && !rangeDragRef.current) return;
			// No button is down any more, so the release happened somewhere we never saw the
			// mouseup — outside the browser window is the common case. Commit here instead:
			// left armed, the drag would keep extending on every later pointer move and would
			// keep handleSelectedCellChange short-circuited for the rest of the session.
			if (event.buttons === 0) {
				finishDrag();
				return;
			}
			dragPointerRef.current = { clientX: event.clientX, clientY: event.clientY };
			extendDragToPointer(event.clientX, event.clientY);
		};
		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', finishDrag);
		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', finishDrag);
		};
	}, [applyFill, extendDragToPointer, writeFillDrag]);

	const isDraggingCells = fillDrag !== null || isRangeDragging;

	// Edge auto-scroll, so a drag can extend past what is on screen the way a spreadsheet
	// does. mousemove alone cannot manage it: once the pointer parks in the edge band it
	// stops firing, so the scroll runs on an interval and re-derives the drag target from
	// the last known pointer position on every tick.
	useEffect(() => {
		if (!isDraggingCells) return undefined;
		const element = gridRef.current && gridRef.current.element;
		if (!element) return undefined;
		const timer = window.setInterval(() => {
			const pointer = dragPointerRef.current;
			if (!pointer) return;
			const bounds = element.getBoundingClientRect();
			let scrollTopDelta = 0;
			if (pointer.clientY < bounds.top + AUTO_SCROLL_EDGE_PX) scrollTopDelta = -AUTO_SCROLL_STEP_PX;
			else if (pointer.clientY > bounds.bottom - AUTO_SCROLL_EDGE_PX) {
				scrollTopDelta = AUTO_SCROLL_STEP_PX;
			}
			// Only a range drag scrolls sideways. A fill is locked to the columns it started
			// from, so panning the grid under it would be pure noise.
			let scrollLeftDelta = 0;
			if (rangeDragRef.current !== null) {
				if (pointer.clientX < bounds.left + AUTO_SCROLL_EDGE_PX) {
					scrollLeftDelta = -AUTO_SCROLL_STEP_PX;
				} else if (pointer.clientX > bounds.right - AUTO_SCROLL_EDGE_PX) {
					scrollLeftDelta = AUTO_SCROLL_STEP_PX;
				}
			}
			if (scrollTopDelta === 0 && scrollLeftDelta === 0) return;
			const scrollTopBefore = element.scrollTop;
			const scrollLeftBefore = element.scrollLeft;
			element.scrollTop = scrollTopBefore + scrollTopDelta;
			element.scrollLeft = scrollLeftBefore + scrollLeftDelta;
			// Already at the end on both axes: nothing moved under the pointer, so there is
			// nothing to re-target either.
			if (element.scrollTop === scrollTopBefore && element.scrollLeft === scrollLeftBefore) {
				return;
			}
			extendDragToPointer(pointer.clientX, pointer.clientY);
		}, AUTO_SCROLL_INTERVAL_MS);
		return () => window.clearInterval(timer);
	}, [extendDragToPointer, isDraggingCells]);

	const originalIndexById = useMemo(() => {
		const map = new Map<number, number>();
		rows.forEach((row, index) => map.set(row._rowId, index));
		return map;
	}, [rows]);

	const columns: Column<ImportRow>[] = useMemo(() => {
		const snoCol: Column<ImportRow> = {
			key: '_sno',
			name: 'S.No.',
			width: 60,
			resizable: false,
			frozen: true,
			cellClass: 'import-grid-sno-cell',
			renderCell: ({ row }) => {
				const originalIndex = originalIndexById.get(row._rowId);
				return (
					<div className="import-grid-sno-content">
						<span>{(originalIndex !== undefined ? originalIndex : 0) + 1}</span>
					</div>
				);
			},
		};

		// Fill the grid: each data column starts from its content-aware base width;
		// when those bases don't span the viewport we scale them up proportionally
		// so the columns consume the available space rather than leaving a gap on
		// the right. The scaled value is assigned as a FIXED pixel width — that's
		// what keeps drag-resize tracking the pointer 1:1 (see MIN_COLUMN_WIDTH).
		// When the bases already overflow (`fillScale` stays 1) we leave them be and
		// let the grid scroll horizontally. rdg keys manual resizes by column key,
		// so a user's drag survives any later refill — only untouched columns move.
		const baseWidths = fields.map((field) =>
			getColumnBaseWidth(field.label, isFieldRequired(field))
		);
		const totalBaseWidth = baseWidths.reduce((total, width) => total + width, 0);
		// 2px slack absorbs per-column floor() rounding so the filled total never
		// nudges one pixel past the viewport and spuriously triggers a scrollbar.
		const availableForData = gridWidth !== null ? gridWidth - FRAME_COLUMNS_WIDTH - 2 : null;
		const fillScale =
			availableForData !== null && totalBaseWidth > 0 && availableForData > totalBaseWidth
				? availableForData / totalBaseWidth
				: 1;

		// The overlay layer every data cell now carries: the selection tint and outline, the
		// fill drag preview, and the fill handle. Readonly columns take part in a selection
		// (they can be copied from) but never host the handle, since a fill cannot write into
		// them.
		const renderDecorations = (
			field: ImportField,
			rowIdx: number,
			columnIdx: number,
			isLastColumn: boolean
		) => {
			const decorations = getCellDecorations(rowIdx, columnIdx);
			return (
				<CellDecorations
					selectionEdges={decorations.selectionEdges}
					copiedEdges={decorations.copiedEdges}
					isFillPreviewCell={decorations.isFillPreviewCell}
					showFillHandle={decorations.showFillHandle && !field.readonly}
					isLastRow={rowIdx === displayRows.length - 1}
					isLastColumn={isLastColumn}
					onFillStart={handleFillStart}
					onFillToLastRow={handleFillToLastRow}
				/>
			);
		};

		const dataCols: Column<ImportRow>[] = fields.map((field, index) => {
			// The delete column, when present, sits to the right of every data column,
			// so nothing is ever flush with the grid's inline edge.
			const isLastDataColumn = !allowDeleteRows && index === fields.length - 1;
			const base: Column<ImportRow> = {
				key: field.key,
				name: field.label,
				resizable: true,
				width: Math.floor(baseWidths[index] * fillScale),
				minWidth: MIN_RESIZE_COLUMN_WIDTH,
				renderHeaderCell: () => <ImportGridHeaderCell field={field} />,
				// import-grid-cell-decorated makes the cell the containing block for the
				// absolutely-positioned overlays rendered by renderDecorations.
				cellClass: (row) => {
					const classes = ['import-grid-cell-decorated'];
					const errs = row._errors && row._errors[field.key];
					if (errs && errs.length > 0) classes.push('import-grid-cell-error');
					if (field.readonly) classes.push('import-grid-cell-readonly');
					// Only the cell hosting the fill handle opts out of rdg's overflow clipping,
					// so the handle can straddle that cell's bottom-right corner.
					if (
						!field.readonly &&
						fillHandleCell !== null &&
						fillHandleCell.rowId === row._rowId &&
						fillHandleCell.columnIdx === index
					) {
						classes.push('import-grid-cell-fill-host');
					}
					return classes.join(' ');
				},
			};

			if (field.renderCustomCell) {
				const customRenderer = field.renderCustomCell;
				return {
					...base,
					editable: false,
					renderCell: ({ row, rowIdx }) => {
						const handleCustomCellChange = (nextValue: any) => {
							const updatedRows = rows.map((existingRow) =>
								existingRow._rowId === row._rowId
									? { ...existingRow, [field.key]: nextValue }
									: existingRow
							);
							handleRowsChange(updatedRows);
						};
						return (
							<>
								<div className="h-full w-full">
									<CellWithErrorTooltip field={field} row={row}>
										{customRenderer({
											value: row[field.key],
											row,
											field,
											onChange: handleCustomCellChange,
										})}
									</CellWithErrorTooltip>
								</div>
								{renderDecorations(field, rowIdx, index, isLastDataColumn)}
							</>
						);
					},
				};
			}

			if (field.readonly) {
				return {
					...base,
					editable: false,
					renderCell: ({ row, rowIdx }) => (
						<>
							<CellWithErrorTooltip field={field} row={row}>
								<CellTextContent value={row[field.key]} />
							</CellWithErrorTooltip>
							{renderDecorations(field, rowIdx, index, isLastDataColumn)}
						</>
					),
				};
			}

			if (field.type === 'enum' && field.config && field.config.options) {
				const options = field.config.options;
				return {
					...base,
					editable: true,
					renderEditCell: (props) => <SelectEditor {...props} options={options} />,
					renderCell: ({ row, rowIdx }) => {
						const val = row[field.key];
						const matched = options.find((option) => String(option.value) === String(val));
						const displayValue = matched ? matched.label : val;
						return (
							<>
								<CellWithErrorTooltip field={field} row={row}>
									<div className="import-grid-enum-cell">
										<CellTextContent value={displayValue} />
										<DownOutlined translate="" className="import-grid-enum-chevron" />
									</div>
								</CellWithErrorTooltip>
								{renderDecorations(field, rowIdx, index, isLastDataColumn)}
							</>
						);
					},
				};
			}

			if (field.type === 'enum-list' && field.config && field.config.options) {
				const options = field.config.options;
				return {
					...base,
					editable: true,
					renderEditCell: (props) => <MultiSelectEditor {...props} options={options} />,
					renderCell: ({ row, rowIdx }) => {
						// Show option labels, not the stored values — a recordHook typically
						// resolves names to ids, so the raw cell value is meaningless to read.
						// Anything with no matching option is shown as typed, and the field's
						// error tooltip explains why it did not resolve.
						const displayValue = toEnumListValues(row[field.key])
							.map((value) => {
								const matched = options.find((option) => String(option.value) === String(value));
								return matched ? matched.label : value;
							})
							.join(', ');
						return (
							<>
								<CellWithErrorTooltip field={field} row={row}>
									<div className="import-grid-enum-cell">
										<CellTextContent value={displayValue} />
										<DownOutlined translate="" className="import-grid-enum-chevron" />
									</div>
								</CellWithErrorTooltip>
								{renderDecorations(field, rowIdx, index, isLastDataColumn)}
							</>
						);
					},
				};
			}

			if (field.type === 'boolean') {
				return {
					...base,
					renderCell: (props) => (
						<>
							<CellWithErrorTooltip field={field} row={props.row}>
								<CheckboxFormatter {...props} />
							</CellWithErrorTooltip>
							{renderDecorations(field, props.rowIdx, index, isLastDataColumn)}
						</>
					),
				};
			}

			if (field.type === 'number') {
				return {
					...base,
					editable: true,
					renderEditCell: NumberEditor,
					renderCell: ({ row, rowIdx }) => (
						<>
							<CellWithErrorTooltip field={field} row={row}>
								<CellTextContent value={row[field.key]} />
							</CellWithErrorTooltip>
							{renderDecorations(field, rowIdx, index, isLastDataColumn)}
						</>
					),
				};
			}

			return {
				...base,
				editable: true,
				renderEditCell: MultilineTextEditor,
				renderCell: ({ row, rowIdx }) => (
					<>
						<CellWithErrorTooltip field={field} row={row}>
							<CellTextContent value={row[field.key]} />
						</CellWithErrorTooltip>
						{renderDecorations(field, rowIdx, index, isLastDataColumn)}
					</>
				),
			};
		});

		const deleteCol: Column<ImportRow> = {
			key: '_delete',
			name: '',
			width: 40,
			resizable: false,
			cellClass: 'import-grid-delete-cell',
			renderCell: ({ row }) => {
				const handleDeleteClick = () => handleDeleteRow(row._rowId);
				const deleteButton = (
					<Button
						type="link"
						size="small"
						className="text-character-secondary"
						icon={<DeleteOutlined translate="" />}
						aria-label="Delete row"
						title="Delete row"
					/>
				);
				// A blank, never-touched row holds nothing worth confirming — wire the
				// click straight to delete. Rows the user has filled in get an inline
				// Popconfirm anchored to the trash icon instead of a blocking dialog.
				if (isRowFullyEmpty(row, fields)) {
					return React.cloneElement(deleteButton, { onClick: handleDeleteClick });
				}
				return (
					<Popconfirm
						title="Delete this row?"
						okText="Delete"
						okType="danger"
						cancelText="Cancel"
						placement="left"
						onConfirm={handleDeleteClick}
					>
						{deleteButton}
					</Popconfirm>
				);
			},
		};

		return allowDeleteRows ? [snoCol, ...dataCols, deleteCol] : [snoCol, ...dataCols];
	}, [
		fields,
		handleDeleteRow,
		originalIndexById,
		rows,
		handleRowsChange,
		gridWidth,
		allowDeleteRows,
		displayRows.length,
		fillHandleCell,
		getCellDecorations,
		handleFillStart,
		handleFillToLastRow,
	]);

	// Render each row alongside a full-width insert-row separator beneath it. When adding rows is
	// disabled the separator is omitted so there's no way to insert between rows.
	const renderRow = useCallback(
		(key: React.Key, props: RenderRowProps<ImportRow>) =>
			allowAddRows ? (
				<React.Fragment key={key}>
					<Row {...props} />
					<RowInsertSeparator
						rowIdx={props.rowIdx}
						rowId={props.row._rowId}
						onInsertRowAfter={handleInsertRowAfter}
					/>
				</React.Fragment>
			) : (
				<Row key={key} {...props} />
			),
		[handleInsertRowAfter, allowAddRows]
	);

	const gridRenderers = useMemo(() => ({ renderRow }), [renderRow]);

	// Single click defaults to SELECT mode (no EDIT) so Cmd+C can fire
	// `onCellKeyDown` below. Edit mode is entered via double-click, Enter, or
	// typing — same contract as Excel / Google Sheets. Exception: enum cells
	// are dropdown-shaped (chevron indicator), so single click also enters
	// EDIT mode for those, matching the original Flatfile importer where the
	// dropdown opens on the first click. Trade-off: copy from a dropdown cell
	// requires pressing Escape first to leave EDIT mode.
	const editOnClickKeys = useMemo(() => {
		const keys = new Set<string>();
		fields.forEach((field) => {
			if (
				field.type === 'enum' &&
				!field.readonly &&
				!field.renderCustomCell &&
				field.config &&
				field.config.options
			) {
				keys.add(field.key);
			}
		});
		return keys;
	}, [fields]);

	// Tracks the cell that received the previous click so a second click on the
	// same already-selected cell can enter EDIT mode — matches the "click to
	// select, click again to rename" pattern users expect from spreadsheet /
	// file-manager UIs. Reset whenever a different cell is clicked.
	const lastClickedCellRef = useRef<{ rowId: number; columnKey: string } | null>(null);

	const handleCellClick = useCallback(
		(args) => {
			// A promoted range drag still produces a click on whichever cell the pointer was
			// released over. Swallow that one so ending a drag back inside the anchor cell
			// does not also open its editor on top of the block just selected. Cleared by the
			// next mousedown, so a drag released outside the grid cannot strand the flag.
			if (suppressNextCellClickRef.current) {
				suppressNextCellClickRef.current = false;
				lastClickedCellRef.current = null;
				return;
			}
			if (editOnClickKeys.has(args.column.key)) {
				args.selectCell(true);
				lastClickedCellRef.current = null;
				return;
			}
			// Skip frame cells — they have their own buttons and aren't
			// editable, so "click to edit" doesn't apply.
			if (args.column.key === '_sno' || args.column.key === '_delete') {
				lastClickedCellRef.current = null;
				return;
			}
			const previous = lastClickedCellRef.current;
			const isSameCell =
				previous && previous.rowId === args.row._rowId && previous.columnKey === args.column.key;
			if (isSameCell) {
				args.selectCell(true);
				lastClickedCellRef.current = null;
				return;
			}
			lastClickedCellRef.current = {
				rowId: args.row._rowId,
				columnKey: args.column.key,
			};
		},
		[editOnClickKeys]
	);

	const handleCellKeyDown = useCallback(
		(args, event) => {
			// Owning copy/paste ourselves — instead of relying on rdg's onCopy/onPaste props —
			// lets us treat custom cells (editable: false) and dropdowns the same as text and
			// number cells, and is what makes a whole-block copy possible at all (rdg has no
			// notion of a range). But rdg fires this in BOTH select and edit mode, so bail in
			// EDIT mode: inside the textarea/input editor, Cmd+C / Cmd+V must hit the native
			// field (copy the selected text, paste at the caret) rather than our cell model.
			if (args.mode === 'EDIT') return;
			if (args.column.key === '_sno' || args.column.key === '_delete') return;
			const focusedColumnIdx = columnIdxByKey.get(args.column.key);
			if (focusedColumnIdx === undefined) return;
			const isMetaOrCtrl = event.ctrlKey || event.metaKey;

			// The block the shortcuts operate on. rdg's focused cell is the fallback: it is
			// always in sync with a single-cell selection, and it is what the very first
			// keystroke after mount has to work from.
			const range = normalisedSelection || {
				firstRowIdx: args.rowIdx,
				lastRowIdx: args.rowIdx,
				firstColIdx: focusedColumnIdx,
				lastColIdx: focusedColumnIdx,
				anchorRowIdx: args.rowIdx,
				anchorColIdx: focusedColumnIdx,
			};

			// Escape dismisses the copy marquee and collapses a block back to the focused
			// cell — the spreadsheet way out of both. Deliberately one press for the pair
			// rather than a hidden two-step. Left alone when there is neither, so rdg's own
			// Escape handling is untouched. The clipboard payload survives: Excel keeps
			// letting you paste after the ants are dismissed, and so does this.
			if (event.key === 'Escape' && !isMetaOrCtrl) {
				const hasBlockToDismiss = copiedBlock !== null || !isSingleCellRange(range);
				if (hasBlockToDismiss) {
					setCopiedBlock(null);
					selectSingleCell(args.rowIdx, focusedColumnIdx);
					event.preventGridDefault();
				}
				return;
			}

			// Shift + arrow extends the block from its anchor, so a range can be built (and
			// copied) without a mouse. preventGridDefault keeps rdg from moving its focused
			// cell, which would drag the anchor along with it.
			const arrowDelta = SELECTION_ARROW_DELTAS[event.key];
			if (event.shiftKey && !isMetaOrCtrl && arrowDelta) {
				const current = selectionRef.current;
				const focusRowIdx = Math.max(
					0,
					Math.min(
						(current ? current.focusRowIdx : args.rowIdx) + arrowDelta.rowDelta,
						displayRows.length - 1
					)
				);
				const focusColIdx = Math.max(
					0,
					Math.min(
						(current ? current.focusColIdx : focusedColumnIdx) + arrowDelta.colDelta,
						fields.length - 1
					)
				);
				writeSelection({
					anchorRowIdx: current ? current.anchorRowIdx : args.rowIdx,
					anchorColIdx: current ? current.anchorColIdx : focusedColumnIdx,
					focusRowIdx,
					focusColIdx,
				});
				// Follow the growing edge, since rdg is no longer scrolling for us.
				if (gridRef.current) gridRef.current.scrollToCell({ rowIdx: focusRowIdx });
				event.preventGridDefault();
				return;
			}

			if (!isMetaOrCtrl) return;
			const key = event.key.toLowerCase();

			// Select every data cell. The usual prelude to "copy the whole grid", and the one
			// case where preventDefault is also needed — otherwise the browser select-all
			// runs and leaves a text selection that the copy guard below would defer to.
			if (key === 'a') {
				if (displayRows.length === 0 || fields.length === 0) return;
				writeSelection({
					anchorRowIdx: 0,
					anchorColIdx: 0,
					focusRowIdx: displayRows.length - 1,
					focusColIdx: fields.length - 1,
				});
				event.preventDefault();
				event.preventGridDefault();
				return;
			}

			if (key === 'c') {
				// Skip when the user has an active text selection in the page — they're copying
				// text, not cells. Mirrors rdg's own guard.
				const textSelection = window.getSelection();
				if (textSelection && !textSelection.isCollapsed) return;

				const columnKeys = fields
					.slice(range.firstColIdx, range.lastColIdx + 1)
					.map((field) => field.key);
				const values: any[][] = [];
				for (let rowIdx = range.firstRowIdx; rowIdx <= range.lastRowIdx; rowIdx += 1) {
					const row = displayRows[rowIdx];
					if (row) values.push(columnKeys.map((columnKey) => row[columnKey]));
				}
				if (columnKeys.length === 0 || values.length === 0) return;
				copiedRangeRef.current = { columnKeys, values };
				// Mark the source block so it keeps its dashed marquee while the user moves the
				// selection to wherever the paste is going.
				setCopiedBlock({
					firstRowIdx: range.firstRowIdx,
					lastRowIdx: range.firstRowIdx + values.length - 1,
					firstColIdx: range.firstColIdx,
					lastColIdx: range.firstColIdx + columnKeys.length - 1,
				});

				// Best-effort mirror to the system clipboard for cross-app copies, as TSV so a
				// block lands in Excel / Sheets as a block. Silently no-ops when blocked
				// (insecure context, permission denial), which is fine: the in-memory range
				// above is what an intra-grid paste reads.
				try {
					if (navigator.clipboard && navigator.clipboard.writeText) {
						navigator.clipboard.writeText(buildTsv(values)).catch(() => undefined);
					}
				} catch (clipboardErr) {
					// Ignore — see above.
				}
				const copiedCellCount = values.length * columnKeys.length;
				message.success(copiedCellCount === 1 ? 'Copied' : `Copied ${copiedCellCount} cells`);
				event.preventGridDefault();
				return;
			}

			if (key === 'v') {
				const copied = copiedRangeRef.current;
				if (!copied) return;
				// The copied block lands with its top-left corner on the top-left of the
				// current selection, exactly like a spreadsheet.
				const updatedRowsByIdx = new Map<number, ImportRow>();
				let pastedCellCount = 0;
				copied.values.forEach((rowValues, rowOffset) => {
					const targetRowIdx = range.firstRowIdx + rowOffset;
					// A paste never grows the grid — the same rule as a fill. Anything hanging
					// past the last row, or past the last column, is dropped.
					if (targetRowIdx > displayRows.length - 1) return;
					rowValues.forEach((value, colOffset) => {
						const targetField = fields[range.firstColIdx + colOffset];
						if (!targetField || targetField.readonly) return;
						const rowToUpdate = updatedRowsByIdx.get(targetRowIdx) || displayRows[targetRowIdx];
						if (!rowToUpdate) return;
						// Deep-copied for the same reason a fill clones: the list-shaped columns
						// hold arrays of objects, and sharing one across pasted rows would let a
						// later edit to one row silently rewrite its neighbours.
						updatedRowsByIdx.set(targetRowIdx, {
							...rowToUpdate,
							[targetField.key]: cloneFillValue(value),
						});
						pastedCellCount += 1;
					});
				});
				if (pastedCellCount === 0) return;
				handleRowsChange(
					displayRows.map((row, rowIdx) => {
						const updated = updatedRowsByIdx.get(rowIdx);
						return updated ? updated : row;
					})
				);
				// Select what was actually written, clamped to the grid, so the user can see
				// where the block went and re-copy it.
				writeSelection({
					anchorRowIdx: range.firstRowIdx,
					anchorColIdx: range.firstColIdx,
					focusRowIdx: Math.min(
						range.firstRowIdx + copied.values.length - 1,
						displayRows.length - 1
					),
					focusColIdx: Math.min(
						range.firstColIdx + copied.columnKeys.length - 1,
						fields.length - 1
					),
				});
				// Only worth announcing for a block — a single pasted value is visible on its
				// own, and the previous behaviour was silent.
				if (pastedCellCount > 1) message.success(`Pasted ${pastedCellCount} cells`);
				event.preventGridDefault();
			}
		},
		[
			columnIdxByKey,
			copiedBlock,
			displayRows,
			fields,
			handleRowsChange,
			normalisedSelection,
			selectSingleCell,
			writeSelection,
		]
	);

	// After Add Row appends to the list (and the new row has rendered), scroll it
	// into view. Keyed on displayRows.length so it runs on the commit that grows
	// the grid; the ref gate ensures only an explicit add triggers a scroll, not
	// every row change (edits, deletes, filter toggles).
	useLayoutEffect(() => {
		if (!scrollToBottomRef.current) return;
		scrollToBottomRef.current = false;
		if (gridRef.current && displayRows.length > 0) {
			gridRef.current.scrollToCell({ rowIdx: displayRows.length - 1 });
		}
	}, [displayRows.length]);

	const getRowClass = useCallback(
		(_row: ImportRow, rowIdx: number) => (rowIdx % 2 === 1 ? 'import-grid-row-alt' : undefined),
		[]
	);

	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="mb-2 flex items-center justify-between">
				<div className="flex items-center">
					{errorRowCount > 0 && (
						<span className="text-character-danger">
							{errorRowCount} row{errorRowCount !== 1 ? 's' : ''} with errors
						</span>
					)}
					<span className="ml-3 text-[#999]">
						{effectiveShowErrorsOnly
							? `${displayRows.length} of ${rows.length} row${rows.length !== 1 ? 's' : ''}`
							: `${rows.length} row${rows.length !== 1 ? 's' : ''}`}
					</span>
					<label
						className={`ml-3 flex items-center gap-1 whitespace-nowrap ${
							errorRowCount > 0 ? 'cursor-pointer' : 'cursor-not-allowed text-[#bbb]'
						}`}
						title={errorRowCount > 0 ? 'Filter to rows with errors only' : 'No rows have errors'}
					>
						<input
							type="checkbox"
							checked={effectiveShowErrorsOnly}
							disabled={errorRowCount === 0}
							onChange={handleToggleShowErrorsOnly}
						/>
						Show errors only
					</label>
				</div>
				{allowAddRows && (
					<Button icon={<PlusOutlined translate="" />} onClick={handleAddRow}>
						Add Row
					</Button>
				)}
			</div>
			<DataGrid
				ref={gridRef}
				columns={columns}
				rows={displayRows}
				rowKeyGetter={(row) => row._rowId}
				onRowsChange={handleRowsChange}
				onCellClick={handleCellClick}
				onCellKeyDown={handleCellKeyDown}
				onSelectedCellChange={handleSelectedCellChange}
				onColumnResize={handleColumnResize}
				rowHeight={GRID_ROW_HEIGHT}
				headerRowHeight={GRID_HEADER_HEIGHT}
				rowClass={getRowClass}
				renderers={gridRenderers}
				className={`import-grid min-h-0 flex-1${
					isHorizontallyScrollable ? ' import-grid-x-scroll' : ''
				}${isDraggingCells ? ' import-grid-dragging-cells' : ''}`}
			/>
			{/* Full-height resize indicator: while a column is being dragged, paint a
			    solid black line down its right edge across the header and every row.
			    rdg sets aria-colindex = column.idx + 1 on both header and body cells,
			    so one selector covers the whole column. */}
			{resizingColIdx !== null ? (
				<style>{`
					.import-grid [aria-colindex="${resizingColIdx + 1}"] {
						box-shadow: inset -2px 0 0 0 #000;
					}
				`}</style>
			) : null}
			<style>{`
				.import-grid {
					--rdg-background-color: #fff;
					--rdg-header-background-color: #fafafa;
					--rdg-row-hover-background-color: #f5f5f5;
					--rdg-color: #262626;
					--rdg-font-size: 14px;
					--rdg-color-scheme: light;
					/* Lighter column/row separators than rdg's default #ddd — matches
					   Ant Design's split-line tone. */
					--rdg-border-color: #f0f0f0;
					/* No shadow on the frozen S.No column by default — with nothing
					   scrolled underneath it the shadow just reads as a smudge against
					   a flush edge. Restored below only when the grid can scroll
					   horizontally. */
					--rdg-cell-frozen-box-shadow: none;
				}
				/* Re-enable rdg's frozen-column shadow only once columns overflow the
				   viewport, so the sticky S.No column casts a shadow over the content
				   scrolling beneath it. */
				.import-grid.import-grid-x-scroll {
					--rdg-cell-frozen-box-shadow: 2px 0 5px -2px rgba(136, 136, 136, 0.3);
				}
				/* Prod CSS minifier (cssnano mergeRules) merges react-data-grid's
				   resize-handle rule with unrelated header-cell rules, leaking
				   inline-size: 10px and position: absolute onto the header cell
				   itself and clipping header text to a single character. Reset
				   on the header cell; the resize handle (direct child) keeps
				   its absolute 10px sizing. */
				.import-grid [role="columnheader"] {
					inline-size: auto;
					position: sticky;
					cursor: default;
					inset-block-start: 0;
					inset-inline-end: auto;
					inset-block-end: auto;
				}
				/* Header label + required marker. The label flexes and truncates so a
				   narrow column clips the text rather than pushing the asterisk out of
				   view; the asterisk never shrinks. */
				.import-grid-header-content {
					display: flex;
					align-items: center;
					gap: 2px;
					min-width: 0;
					width: 100%;
				}
				.import-grid-header-label {
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
					min-width: 0;
				}
				.import-grid-header-required {
					color: #ff4d4f;
					font-family: SimSun, sans-serif;
					line-height: 1;
					flex-shrink: 0;
				}
				/* Same prod-only cssnano fallout as the header reset above, but on
				   the cell in EDIT mode: the flattened resize-handle rule leaks
				   position: absolute / inline-size: 10px / inset: 0 onto the editing
				   cell (rdg adds .rdg-editor-container there). That collapses the
				   enum/text editor to a ~10px sliver pinned to the cell's right edge,
				   so the Ant Select has no width and its dropdown anchors to nothing
				   and never opens. Pin the editing cell back to its grid track. */
				.import-grid .rdg-editor-container {
					position: relative;
					inline-size: auto;
					inset-block-start: auto;
					inset-inline-end: auto;
					inset-block-end: auto;
					overflow: visible;
				}
				.import-grid ::-webkit-scrollbar {
					width: 8px;
					height: 8px;
				}
				.import-grid ::-webkit-scrollbar-track {
					background: #f1f1f1;
				}
				.import-grid ::-webkit-scrollbar-thumb {
					background: #c1c1c1;
					border-radius: 4px;
				}
				.import-grid ::-webkit-scrollbar-thumb:hover {
					background: #a8a8a8;
				}
				.import-grid-cell-readonly {
					background-color: #f5f5f5 !important;
					color: #999 !important;
					cursor: not-allowed;
				}
				.import-grid-cell-error {
					background-color: #fff2f0 !important;
					border-color: #ff4d4f !important;
				}
				/* Zebra striping for alternate rows. Error/readonly cell colors
				   override via !important so this is a safe per-row default. */
				.import-grid .rdg-row.import-grid-row-alt {
					background-color: #fafafa;
				}
				.import-grid-multiline-editor {
					display: block;
					width: 100%;
					/* Auto-grown to content height in JS (see autoSizeTextarea),
					   capped at the cell so the cell's align-content: center keeps the
					   text in the same spot as the read-only state. Horizontal padding
					   matches the cell's so the text doesn't shift sideways either (the
					   editing cell itself has padding: 0). */
					height: auto;
					max-height: 100%;
					padding: 0 8px;
					border: none;
					outline: none;
					resize: none;
					overflow-y: auto;
					font: inherit;
					line-height: 22px;
					/* Transparent so the cell's (zebra-striped) row background shows
					   through, matching the read-only state instead of forcing white. */
					background-color: transparent;
				}
				/* Vertically center the Ant Select inside the cell when editing
				   an enum value. Without this, the Select's ~32px tall selector
				   sits at the top of the 72px cell and the row appears to
				   "jump" on entering edit mode. */
				.import-grid-enum-editor {
					display: flex;
					align-items: center;
					width: 100%;
					height: 100%;
				}
				.import-grid-enum-editor .ant-select {
					width: 100%;
				}
				.import-grid-sno-cell {
					padding: 0 !important;
				}
				/* Center the delete action button both axes within its narrow column
				   instead of letting it hang off the cell's inline padding. */
				.import-grid-delete-cell {
					padding: 0 !important;
					display: flex;
					align-items: center;
					justify-content: center;
				}
				.import-grid-sno-content {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 100%;
					height: 100%;
				}
				/* Row-insert separator: a full-width hover zone straddling each row's
				   bottom border, injected as an absolutely-positioned sibling of the
				   row (see RowInsertSeparator). It sits above cells so hovering
				   anywhere along the boundary reveals the line + insert button. */
				.import-grid-row-separator {
					position: absolute;
					left: 0;
					right: 0;
					height: 12px;
					transform: translateY(-50%);
					/* Below react-data-grid's sticky header cells (z-index 2 regular /
					   3 frozen) so a separator scrolled up to the top tucks BEHIND the
					   header instead of floating over it — the opaque header then both
					   hides it and intercepts the click. Still above the body data cells
					   (z-index 0) so the insert button stays visible and clickable while
					   the separator is in view. */
					z-index: 1;
					display: flex;
					align-items: center;
					justify-content: center;
				}
				.import-grid-row-separator::before {
					content: '';
					position: absolute;
					left: 0;
					right: 0;
					height: 2px;
					background-color: transparent;
					transition: background-color 0.1s ease;
				}
				.import-grid-row-separator:hover::before {
					background-color: #000000;
				}
				.import-grid-insert-row-btn {
					position: relative;
					z-index: 1;
					width: 18px;
					height: 18px;
					padding: 0;
					display: none;
					align-items: center;
					justify-content: center;
					border: 1px solid #000000;
					border-radius: 50%;
					background-color: #fff;
					color: #000000;
					cursor: pointer;
					font-size: 10px;
					line-height: 1;
					box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
				}
				.import-grid-insert-row-btn:hover {
					background-color: #000000;
					color: #fff;
				}
				.import-grid-row-separator:hover .import-grid-insert-row-btn {
					display: inline-flex;
				}
				/* Take the row-insert separators out of the hit-testing path for as long as a
				   drag is in flight, so (a) the insert-row button doesn't flash open under the
				   dragging cursor, and (b) the 12px band straddling every row boundary can't
				   mask the pointer position the drag is derived from. */
				.import-grid.import-grid-dragging-cells .import-grid-row-separator {
					pointer-events: none;
				}
				/* No text selection while a drag is in flight — the pointer sweeps across cell
				   text on its way down the column or across the row. */
				.import-grid.import-grid-dragging-cells {
					user-select: none;
				}
				/* Every data cell is the containing block for its own overlay layer: the range
				   highlight, the fill preview and (on one cell) the fill handle. */
				.import-grid-cell-decorated {
					position: relative;
				}
				/* The handle straddles the cell's bottom-right corner, but rdg clips cells
				   (overflow: clip) — so the one cell hosting it opts out of clipping. Cell
				   content is unaffected: every custom cell root already carries its own
				   overflow-hidden. Note the cell must NOT take a z-index here — that would open
				   a stacking context and trap the handle's z-index inside the cell, back under
				   the row separator. */
				.import-grid-cell-fill-host {
					overflow: visible;
				}
				/* Excel-style range highlight: a translucent tint over every cell in the block,
				   plus a 1px accent line on the block's OUTER edges only, so a multi-cell
				   selection reads as one rectangle rather than a grid of individually boxed
				   cells. Drawn on an absolutely positioned overlay, so it neither affects layout
				   nor needs !important to sit above the zebra and error cell backgrounds, and
				   inert to the pointer so a click still lands on the cell underneath.
				   border-style is set on the base rule and only the widths are per-edge (see
				   .import-grid-block-edge-*): the four sides compose that way, whereas four
				   box-shadow or background-image rules would each overwrite the last, losing an
				   edge on any corner cell. That is also what lets the copy marquee below reuse
				   the very same edge classes with a dashed style instead of a solid one. */
				.import-grid-range-highlight {
					box-sizing: border-box;
					position: absolute;
					inset: 0;
					background-color: rgba(102, 175, 233, 0.16);
					border: 0 solid var(--rdg-selection-color);
					pointer-events: none;
				}
				/* The anchor cell — the one the gesture started from, and the one rdg keeps its
				   own focus outline on — stays clear, so the tint reads as what the selection
				   added to it. Same convention as Excel's active cell. */
				.import-grid-range-highlight.import-grid-range-anchor {
					background-color: transparent;
				}
				/* Excel's copy marquee: a dashed outline round the block the last Cmd+C took,
				   so it stays obvious what is on the clipboard once the selection has moved on to
				   the paste target. Dark neutral rather than the selection blue precisely so the
				   two can be told apart when they overlap — which they do immediately after a
				   copy, when the same block is both selected and copied. Rendered after the tints
				   so the dashes stay readable over them.
				   Not animated: the classic marching-ants march needs one continuous overlay per
				   block, and this outline is assembled per cell (each cell drawing only the block
				   edges it sits on), so an animated dash phase would visibly restart at every
				   cell boundary. Static dashes read correctly at any block size. */
				.import-grid-copied-marquee {
					box-sizing: border-box;
					position: absolute;
					inset: 0;
					border: 0 dashed #262626;
					pointer-events: none;
				}
				/* Width-only, so these compose across corners and serve both block overlays. */
				.import-grid-block-edge-top {
					border-top-width: 1px;
				}
				.import-grid-block-edge-right {
					border-right-width: 1px;
				}
				.import-grid-block-edge-bottom {
					border-bottom-width: 1px;
				}
				.import-grid-block-edge-left {
					border-left-width: 1px;
				}
				/* Geometry copied from rdg's DragHandle: a square pulled back by half its own
				   size (less the 1px border) on both axes, so it is centred on the corner and
				   bleeds into the cell to the right and the row below. Growing on hover just
				   re-reads the variable, keeping it centred as it doubles. */
				.import-grid-fill-handle {
					--import-grid-fill-handle-size: 8px;
					box-sizing: border-box;
					position: absolute;
					width: var(--import-grid-fill-handle-size);
					height: var(--import-grid-fill-handle-size);
					inset-inline-end: calc(var(--import-grid-fill-handle-size) * -0.5 + 1px);
					inset-block-end: calc(var(--import-grid-fill-handle-size) * -0.5 + 1px);
					background-color: var(--rdg-selection-color);
					cursor: crosshair;
					/* Above the neighbouring cells it overhangs, and above the row-insert
					   separator (z-index 1) whose full-width hover band straddles the same
					   corner and would otherwise take the mousedown. */
					z-index: 2;
				}
				.import-grid-fill-handle:hover {
					--import-grid-fill-handle-size: 16px;
					border: 2px solid var(--rdg-selection-color);
					background-color: var(--rdg-background-color);
				}
				.import-grid-fill-handle-flush-block {
					inset-block-end: 0;
				}
				.import-grid-fill-handle-flush-inline {
					inset-inline-end: 0;
				}
				/* Tint for the rows a fill is about to overwrite. Translucent so the value
				   being replaced stays readable underneath, and inert so it can't intercept
				   the pointer mid-drag. */
				.import-grid-fill-preview {
					position: absolute;
					inset: 0;
					background-color: rgba(102, 175, 233, 0.18);
					pointer-events: none;
				}
				/* Multi-line cell content: wrap long values across the available
				   row height. Native title attribute on the wrapper still
				   surfaces the full value on hover when content exceeds the
				   visible area. */
				.import-grid-cell-content {
					display: -webkit-box;
					-webkit-line-clamp: 3;
					-webkit-box-orient: vertical;
					width: 100%;
					white-space: pre-wrap;
					overflow: hidden;
					overflow-wrap: anywhere;
					word-break: break-word;
					text-overflow: ellipsis;
				}
				/* Enum cells render value + chevron to signal "this is a
				   dropdown" — matches the original Flatfile UI so the user
				   doesn't have to discover the interaction. */
				.import-grid-enum-cell {
					display: flex;
					align-items: center;
					justify-content: space-between;
					width: 100%;
					height: 100%;
					gap: 4px;
					cursor: pointer;
				}
				.import-grid-enum-chevron {
					color: rgba(0, 0, 0, 0.45);
					font-size: 10px;
					flex-shrink: 0;
				}
			`}</style>
		</div>
	);
};

export default EditableImportGrid;
